/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/

import * as React from 'react';
import { getOverrides, mergeOverrides } from '../helpers/overrides';
import DeleteAlt from '../icon/delete-alt';
import TriangleDownIcon from '../icon/triangle-down';
import SearchIconComponent from '../icon/search';
import { LocaleContext } from '../locale';
import type { Locale } from '../locale';
import { STATE_CHANGE_TYPES as MENU_STATE_CHANGE_TYPES } from '../menu/constants';
import { Popover, PLACEMENT, ACCESSIBILITY_TYPE } from '../popover';
import { UIDConsumer } from 'react-uid';
import AutosizeInput from './autosize-input';
import { TYPE, STATE_CHANGE_TYPE, SIZE } from './constants';
import defaultProps from './default-props';
import SelectDropdown from './dropdown';
import { StyledRoot, StyledControlContainer, StyledPlaceholder, StyledValueContainer, StyledInputContainer, StyledIconsContainer, StyledSearchIconContainer, StyledLoadingIndicator } from './styled-components';
import type { SelectProps, SelectState, Value, Option, ChangeAction } from './types';
import { expandValue, normalizeOptions } from './utils';
import { CaretAffordanceDropdownListItem, SelectCaretSessionContext, consumePointerMoved, focusActiveListboxOption, focusSelectedOptionAfterAffordanceOpen, registerPointerTracking, scheduleCaretCloseFocusRestore } from './a11y-helpers';
import type { SyntheticEvent, ChangeEvent } from 'react';

// Shared visually-hidden style used for screen-reader-only spans (e.g. caret
// affordance value + action labels). Matches the pattern from phone-input.
const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0
};

// @ts-ignore
function Noop() {
  return null;
}

// @ts-ignore
const isClick = event => event.type === 'click';
// @ts-ignore
const isLeftClick = event => event.button !== null && event.button !== undefined && event.button === 0;

// @ts-ignore
const containsNode = (parent, child) => {
  if (__BROWSER__) {
    return child && parent && parent.contains((child as any));
  }
};

// Ensures only ONE caret-affordance Select dropdown is open at a time across the
// page. The caret button opens/closes the dropdown imperatively and
// stopPropagation()s its activation, so the Select's native focus path (which
// would otherwise register the outside-click dismissal) never runs — without
// this coordinator, opening a second caret Select leaves the first open too.
// On open, a caret Select closes whichever other one is still registered here.
let activeCaretSelectCloser: (() => void) | null = null;
export function isInteractive(rootTarget: EventTarget, rootElement: Element) {
  if (rootTarget instanceof Element) {
    let target: Element | undefined | null = rootTarget;
    while (target && target !== rootElement) {
      const role = target.getAttribute('role');
      if (role === 'button' || role === 'link') {
        return true;
      }
      if (target.tagName) target = target.parentElement;
    }
  }
  return false;
}
class Select extends React.Component<SelectProps, SelectState> {
  static defaultProps = defaultProps;

  // anchor is a ref that refers to the outermost element rendered when the dropdown menu is not
  // open. This is required so that we can check if clicks are on/off the anchor element.
  anchor = React.createRef<HTMLElement>();
  // dropdown is a ref that refers to the popover element. This is required so that we can check if
  // clicks are on/off the dropdown element.
  dropdown = React.createRef<HTMLElement>();
  input?: HTMLInputElement;
  // dragging is a flag to track whether a mobile device is currently scrolling versus clicking.
  // @ts-ignore
  dragging: boolean;
  // focusAfterClear is a flag to indicate that the dropdowm menu should open after a selected
  // option has been cleared.
  // @ts-ignore
  focusAfterClear: boolean;
  // openAfterFocus is a flag to indicate that the dropdown menu should open when the component is
  // focused. Developers have the option to disable initial clicks opening the dropdown menu. If not
  // disabled, clicks will set this flag to true. Upon focusing, look to this to see if the menu should
  // be opened, or only focus.
  // @ts-ignore
  openAfterFocus: boolean;
  // When an item is selected, it also triggers handleClickOutside and since the selected item is
  // already out of the menu (DOM), it will not recognize it as a subnode and triggers handleBlur
  // that sets isOpen to false. That's a faulty logic causing visible problems when
  // closeOnSelect is false. This flag helps to detect that selection was just made.
  // @ts-ignore
  justSelected: boolean;

  // the select components can accept an array of options or an object where properties are optgroups
  // and values are arrays of options. this class property is constructed and updated in a normalized
  // shape where optgroup titles are stored on the option in the __optgroup field.
  options: Value = [];

  // One-shot flag: set true when the dropdown is opened via the caret affordance
  // button (click or key) so componentDidUpdate moves DOM focus onto the
  // selected option on that open. Reset immediately after use.
  _openedViaAffordance = false;
  // Persistent (whole-open-session) flag mirroring the affordance open. Drives
  // the dropdown's keyboardControlNode routing: while true the dropdown receives
  // keyboardControlNode=null so the <ul> listbox owns its own onKeyDown (focus
  // lives on a portalled <li>, not the ControlContainer the default listener is
  // bound to). Set BEFORE setState so the menu captures it at construction;
  // cleared on close.
  _keyboardFocusInListbox = false;
  // Ref to the caret button element, used to restore focus when the dropdown closes.
  _caretRef = React.createRef<HTMLElement>();
  // Set by the in-listbox Tab handler so the close-focus restore advances focus to
  // the adjacent field (Tab → next, Shift+Tab → previous) instead of the caret.
  // Null for every other close path (Esc, selection, outside click).
  _pendingTabDirection: 'forward' | 'backward' | null = null;
  // The listboxId from UIDConsumer, stored so componentDidUpdate can find the listbox.
  _listboxId = '';
  // Per-open-session ref shared (via SelectCaretSessionContext) with every caret
  // DropdownListItem. False until the user deliberately moves the highlight
  // (arrow / type-ahead / genuine hover / click); gates "center the selected
  // option on initial open only" so navigation doesn't re-center on each move.
  _sessionHighlightMovedRef: {
    current: boolean;
  } = {
    current: false
  };
  // Cleanup for the per-document pointer-move tracking installed on mount in
  // caret mode (drops scroll-induced mouseenter highlights).
  _removePointerTracking: (() => void) | null = null;
  // Removes the document-level outside-click listener added while a caret
  // dropdown is open (so clicking elsewhere dismisses it).
  _removeOutsideClick: (() => void) | null = null;
  constructor(props: SelectProps) {
    super(props);
    this.options = normalizeOptions(props.options);
  }

  // @ts-ignore
  state = {
    // @ts-ignore
    activeDescendant: null,
    inputValue: '',
    isFocused: false,
    isOpen: this.props.startOpen,
    isPseudoFocused: false,
    openedViaKeyboard: false,
    // Structured intent for the caret-affordance polite aria-live region (see
    // announceValueChange). The final string is localized in render where the
    // LocaleContext is available. The nonce forces a DOM text change — and thus a
    // re-announcement — even when the same logical message repeats (e.g. removing,
    // re-adding, then removing the same value), via a trailing zero-width space
    // that screen readers don't voice.
    ariaLiveType: ('' as '' | 'removed' | 'cleared'),
    ariaLiveValue: '',
    ariaLiveCount: 0,
    ariaLiveNonce: 0
  };
  isItMounted = false;
  componentDidMount() {
    if (this.props.autoFocus) {
      this.focus();
    }
    this.isItMounted = true;

    // Install per-document pointer-move tracking for the lifetime of a caret
    // Select (refcounted across concurrent selects). Lets the caret list items
    // drop scroll-induced mouseenter highlights (the iOS VoiceOver scroll-lock
    // / highlight ping-pong fix). Resolve the owning document from the caret
    // button when available, else fall back to the global document.
    if (__BROWSER__ && this.props.caretAffordance) {
      const ownerDoc = this._caretRef.current?.ownerDocument ?? document;
      this._removePointerTracking = registerPointerTracking(ownerDoc);
    }
    const {
      controlRef
    } = this.props;
    if (controlRef && typeof controlRef !== 'function') {
      controlRef.current = {
        setDropdownOpen: this.handleDropdownOpen.bind(this),
        setInputValue: this.handleSetInputValue.bind(this),
        setInputFocus: this.handleSetInputFocus.bind(this),
        setInputBlur: this.handleSetInputBlur.bind(this),
        // `focus` & `blur` below are for backwards compatibility and may be removed. Use setInputFocus and setInputBlur instead.
        focus: this.handleSetInputFocus.bind(this),
        blur: this.handleSetInputBlur.bind(this)
      };
    }
  }
  componentDidUpdate(prevProps: SelectProps, prevState: SelectState) {
    if (__BROWSER__) {
      if (prevState.isOpen !== this.state.isOpen) {
        if (this.state.isOpen) {
          this.props.onOpen && this.props.onOpen();
          document.addEventListener('touchstart', this.handleTouchOutside);
          // When the dropdown was opened via the caret affordance button, move
          // real DOM focus onto the SELECTED OPTION (not the bare <ul>): iOS
          // VoiceOver can't follow aria-activedescendant on a focused container,
          // so focus must live on a roving <li> for arrow / swipe navigation to
          // work (see CaretAffordanceDropdownListItem). The one-shot flag is
          // reset after use; the persistent _keyboardFocusInListbox flag keeps
          // keys routed to the listbox for the whole open session.
          if (this._openedViaAffordance) {
            this._openedViaAffordance = false;
            const ownerDoc = this._caretRef.current?.ownerDocument ?? document;
            // New open session: re-pin the highlight to the selected option until
            // the user deliberately moves it (centers on initial open only).
            this._sessionHighlightMovedRef.current = false;
            // Drop any stale pointer-moved flag so the portal-appearance
            // mouseenter under a stationary cursor is treated as scroll-induced.
            consumePointerMoved(ownerDoc);
            // Single-open: close any other caret Select still open, then register
            // ourselves as the active one.
            if (activeCaretSelectCloser && activeCaretSelectCloser !== this._closeSelf) {
              activeCaretSelectCloser();
            }
            activeCaretSelectCloser = this._closeSelf;
            this._addOutsideClick(ownerDoc);
            focusSelectedOptionAfterAffordanceOpen(ownerDoc, this._listboxId);
          }
        } else {
          this.props.onClose && this.props.onClose();
          document.removeEventListener('touchstart', this.handleTouchOutside);
          // The listbox is gone; the next open re-decides key routing.
          this._keyboardFocusInListbox = false;
          this._removeOutsideClick?.();
          this._removeOutsideClick = null;
          if (activeCaretSelectCloser === this._closeSelf) {
            activeCaretSelectCloser = null;
          }
          // When caretAffordance is active, restore focus on close. Normally this
          // lands on the caret button so the AT cursor stays in the field; when
          // the close was triggered by Tab / Shift+Tab inside the listbox, it
          // advances to the adjacent field instead (one Tab both closes and moves
          // on). The restore re-asserts on a few later frames if the Popover
          // teardown drops focus to <body>.
          if (this.props.caretAffordance) {
            const tabDirection = this._pendingTabDirection;
            this._pendingTabDirection = null;
            scheduleCaretCloseFocusRestore({
              caretRef: this._caretRef,
              tabDirection
            });
          }
        }
      }
      if (!prevState.isFocused && this.state.isFocused) {
        setTimeout(() => document.addEventListener('click', this.handleClickOutside), 0);
      }
    }
  }
  componentWillUnmount() {
    if (__BROWSER__) {
      document.removeEventListener('touchstart', this.handleTouchOutside);
      document.removeEventListener('click', this.handleClickOutside);
      this._removeOutsideClick?.();
      this._removeOutsideClick = null;
      this._removePointerTracking?.();
      this._removePointerTracking = null;
      if (activeCaretSelectCloser === this._closeSelf) {
        activeCaretSelectCloser = null;
      }
    }
    this.isItMounted = false;
  }

  // Stable close callback registered in the page-wide single-open coordinator
  // (activeCaretSelectCloser) so opening one caret Select dismisses any other.
  _closeSelf = () => {
    this.setState({
      isOpen: false
    });
  };

  // Mark that the user has deliberately moved the menu highlight this open
  // session (arrow / type-ahead / genuine hover / click). Hands the "center on
  // initial open only" logic in CaretAffordanceDropdownListItem over to the
  // menu's gentle scrollItemIntoView for the rest of the session.
  _markSessionHighlightMoved = () => {
    this._sessionHighlightMovedRef.current = true;
  };

  // Listeners installed for the lifetime of a caret dropdown being open:
  //   1. mousedown outside → dismiss. The caret button's stopPropagation
  //      bypasses the Select's native focus path, so its built-in click-outside
  //      listener is never wired in this mode — this restores outside-click
  //      dismissal and, with the coordinator, prevents two caret dropdowns
  //      staying open.
  //   2. Space keydown while focus is on a roving option → preventDefault. Focus
  //      lives on a portalled <li> (not a <button>), so the browser default for
  //      Space is to scroll the page. The menu's onKeyDown ignores Space (no
  //      type-ahead here), so we just stop the page scroll. Arrow keys / Enter
  //      are untouched and keep driving navigation / selection.
  _addOutsideClick = (ownerDoc: Document) => {
    this._removeOutsideClick?.();
    const handleMouseDownOutside = (event: MouseEvent) => {
      const target = (event.target as Node | null);
      if (!target) {
        return;
      }
      const selectRoot = this._caretRef.current?.closest<HTMLElement>('[data-baseweb="select"]');
      if (selectRoot && selectRoot.contains(target)) {
        return;
      }
      // The whole portalled dropdown container (options, padding, scrollbar) is
      // "inside" — not just the <ul>.
      if (this.dropdown.current && this.dropdown.current.contains(target)) {
        return;
      }
      this.setState({
        isOpen: false
      });
    };
    const handleSpaceInListbox = (event: KeyboardEvent) => {
      if (event.key !== ' ' && event.key !== 'Spacebar') {
        return;
      }
      const listbox = ownerDoc.getElementById(this._listboxId);
      if (listbox && listbox.contains(ownerDoc.activeElement)) {
        event.preventDefault();
      }
    };
    //   3. Tab / Shift+Tab while focus is on a roving option → dismiss the
    //      dropdown and advance focus to the adjacent field. The listbox is
    //      portalled at the end of the DOM, so letting the browser advance focus
    //      naturally would jump to the top/bottom of the page; instead we
    //      preventDefault, record the direction, close, and let the close-focus
    //      restore move focus to the next/previous field. The value is left
    //      unchanged (Tab does not commit a highlighted option). Capture phase so
    //      we run before the listbox's own keydown handler.
    const handleTabInListbox = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }
      const listbox = ownerDoc.getElementById(this._listboxId);
      if (!listbox || !listbox.contains(ownerDoc.activeElement)) {
        return;
      }
      event.preventDefault();
      this._pendingTabDirection = event.shiftKey ? 'backward' : 'forward';
      this.setState({
        isOpen: false
      });
    };
    ownerDoc.addEventListener('mousedown', handleMouseDownOutside, true);
    ownerDoc.addEventListener('keydown', handleSpaceInListbox, true);
    ownerDoc.addEventListener('keydown', handleTabInListbox, true);
    this._removeOutsideClick = () => {
      ownerDoc.removeEventListener('mousedown', handleMouseDownOutside, true);
      ownerDoc.removeEventListener('keydown', handleSpaceInListbox, true);
      ownerDoc.removeEventListener('keydown', handleTabInListbox, true);
    };
  };
  focus() {
    if (!this.input) return;
    this.input.focus();
  }

  // Focus the caret affordance button when active, otherwise focus the internal
  // input. Used by removeValue / clearValue so focus isn't stranded on the
  // aria-hidden combobox input after a tag removal or field clear.
  //
  // In caretAffordance mode we can't just call caret.focus() once: the activated
  // element (the removed Tag, or the cleared value container) unmounts on the
  // value-change re-render, which drops focus a frame later — and Android
  // TalkBack does not follow a one-shot programmatic focus() back onto the caret.
  // Reuse the same durable, multi-frame restore the dropdown-close path uses
  // (sync + rAF + a few delayed passes) so focus is re-asserted on the caret
  // after the DOM settles. tabDirection is null here: tag removal / clear should
  // always land on the caret, never advance to an adjacent field.
  //
  // KNOWN LIMITATION (Android TalkBack + multi-select, 2nd+ dismissal): when a
  // multi-select is emptied by re-selecting options in the open dropdown, the
  // dismissal that removes the LAST tag can leave TalkBack's accessibility
  // cursor on the (still-painted) placeholder text to the left of the caret
  // rather than on the caret button. DOM focus IS correctly restored to the
  // caret here, but TalkBack does not reliably follow a programmatic focus()
  // across the dropdown-portal teardown, so its virtual cursor stays put. The
  // control remains fully operable (a single swipe-right reaches the caret) and
  // earlier dismissals (where a tag still remains) behave correctly. This is a
  // screen-reader focus-placement quirk with a workaround, tracked as a low
  // severity (P4) a11y issue; attempts to force the cursor (native <button>
  // caret, blur()+refocus re-assert, hiding the empty value container) did not
  // reliably move TalkBack's cursor and were reverted to keep the code simple.
  focusAffordanceOrInput() {
    if (this.props.caretAffordance && this._caretRef.current) {
      scheduleCaretCloseFocusRestore({
        caretRef: this._caretRef,
        tabDirection: null
      });
    } else {
      this.focus();
    }
  }

  // Record a change for the caret-affordance polite aria-live region. On Android
  // TalkBack the accessibility cursor can't be moved onto the caret after a tag
  // removal (a proven platform limitation — programmatic focus doesn't move the
  // AT cursor and mutating the a11y tree only adds flake), so instead of trying
  // to relocate the cursor we ANNOUNCE the change: the user hears spoken
  // confirmation ("English removed. 2 selected.") wherever their cursor sits.
  // Scoped to caretAffordance so the default Select (thousands of consumers) is
  // unchanged. The nonce bump guarantees re-announcement of repeated messages.
  announceValueChange(type: 'removed' | 'cleared', value: string, count: number) {
    if (!this.props.caretAffordance) return;
    this.setState({
      ariaLiveType: type,
      ariaLiveValue: value,
      ariaLiveCount: count,
      ariaLiveNonce: this.state.ariaLiveNonce + 1
    });
  }

  // Localize the pending aria-live announcement (LocaleContext passed in from the
  // render Consumer). Falls back to English if a locale omits the optional keys.
  // The trailing zero-width space (toggled by the nonce) makes an otherwise
  // identical repeated message a distinct DOM text node so the live region fires
  // again; U+200B is not spoken by screen readers.
  composeAriaLiveMessage(locale: Locale): string {
    const {
      ariaLiveType,
      ariaLiveValue,
      ariaLiveCount,
      ariaLiveNonce
    } = this.state;
    let message = '';
    if (ariaLiveType === 'removed') {
      const template = locale.select.valueRemovedAnnouncement ?? '${value} removed. ${count} selected.';
      message = template.replace('${value}', ariaLiveValue).replace('${count}', String(ariaLiveCount));
    } else if (ariaLiveType === 'cleared') {
      message = locale.select.selectionClearedAnnouncement ?? 'Selection cleared.';
    }
    if (!message) {
      return '';
    }
    return ariaLiveNonce % 2 === 0 ? message : `${message}\u200B`;
  }
  handleDropdownOpen(nextOpenState: boolean) {
    this.setState({
      isOpen: nextOpenState
    });
  }
  handleSetInputValue(newInputValue: string) {
    this.setState({
      inputValue: newInputValue
    });
  }
  handleSetInputFocus() {
    // @ts-ignore
    this.input.focus();
  }
  handleSetInputBlur() {
    // @ts-ignore
    this.input.blur();
  }

  // Handle touch outside on mobile to dismiss menu, ensures that the
  // touch target is not within the anchor DOM node.
  handleTouchOutside = (event: TouchEvent) => {
    if (containsNode(this.dropdown.current, event.target)) return;
    if (!containsNode(this.anchor.current, event.target)) {
      this.closeMenu();
    }
  };

  // Track dragging state to filter false-positive actions where a user
  // intends to drag/scroll the page.
  handleTouchMove = () => this.dragging = true;
  handleTouchStart = () => this.dragging = false;
  handleTouchEnd = (event: TouchEvent) => {
    if (this.dragging) return;
    this.handleClick(event);
  };
  handleClick = (event: MouseEvent | TouchEvent) => {
    if (this.props.disabled || !isClick(event) && !isLeftClick(event)) {
      return;
    }

    // Case comes up when text has been typed into the input field. If no text provided,
    // the 'input' element will have essentially 0 width therefore will not be clickable.
    // When click outside does not reset input, text provided will stay rendered after clicks away
    // from the select component. Upon subsequent clicks on the provided text, open the dropdown
    // menu, in addition to text edit operations.
    if (event.target === this.input) {
      // CHASE: not sure why this condition is here. I cannot replicate a situation where clicks
      // on provided text break into here.
      if (!this.state.isFocused) {
        // @ts-ignore
        this.openAfterFocus = this.props.openOnClick;
        this.focus();
      }
      if (!this.state.isOpen) {
        this.setState({
          isOpen: true,
          isFocused: true,
          isPseudoFocused: false
        });
      }
      return;
    }

    // Ensures that interactive elements within the Select component do not trigger the outer click
    // handler. For example, after an option is selected clicks on the 'clear' icon call here. We
    // should ignore those events. This comes after case where click is on input element, so that
    // those are handled on their own.
    // @ts-ignore
    if (this.input && isInteractive(event.target, this.input)) {
      return;
    }

    // For the simple case where clicking on the Select does not allow for providing
    // text input to filter the dropdown options.
    if (!this.props.searchable) {
      this.focus();
      if (this.state.isOpen) {
        this.setState({
          isOpen: false,
          isFocused: false,
          openedViaKeyboard: false
        });
      } else {
        this.setState({
          isOpen: true,
          isFocused: true,
          openedViaKeyboard: false
        });
      }
      return;
    }

    // Cases below only apply to searchable Select component.
    if (this.state.isFocused) {
      // iOS ignores programmatic calls to input.focus() that were not triggered by a click event.
      // This component can get into a state where isFocused is true, but the DOM node is not
      // focused. Call focus here again to ensure.
      this.focus();

      // Case comes up when click outside does not reset input - once text has been provided to
      // the input, and the user closes the dropdown menu the provided text is maintained. After
      // this, if the user focuses back into the select component then clicks on the component,
      // the provided text highlights rather than position's the cursor at the end of the input.
      if (this.input) this.input.value = '';
      this.setState(prev => ({
        isOpen: !this.focusAfterClear && !prev.isOpen,
        isPseudoFocused: false
      }));
      this.focusAfterClear = false;
    } else {
      // When clear button is clicked, need to click twice to open control container - https://github.com/uber/baseweb/issues/4285
      // Setting focusAfterClear to false, resolves the issue
      this.focusAfterClear = false;
      // @ts-ignore
      this.openAfterFocus = this.props.openOnClick;
      this.focus();
    }
  };
  closeMenu() {
    if (this.props.onCloseResetsInput) {
      this.setState({
        inputValue: '',
        isOpen: false,
        isPseudoFocused: this.state.isFocused && !this.props.multi,
        openedViaKeyboard: false
      });
    } else {
      this.setState({
        isOpen: false,
        isPseudoFocused: this.state.isFocused && !this.props.multi,
        openedViaKeyboard: false
      });
    }
  }
  handleInputFocus = (event: SyntheticEvent<HTMLElement>) => {
    if (this.props.disabled) return;
    if (this.props.onFocus) this.props.onFocus(event);
    let toOpen = this.state.isOpen || this.openAfterFocus;
    // if focus happens after clear values, don't open dropdown yet.
    toOpen = !this.focusAfterClear && toOpen;
    this.setState({
      isFocused: true,
      isOpen: !!toOpen,
      openedViaKeyboard: false
    });
    this.focusAfterClear = false;
    this.openAfterFocus = false;
  };
  handleBlur = (event: React.FocusEvent | MouseEvent) => {
    if (event.relatedTarget) {
      if (containsNode(this.anchor.current, event.relatedTarget) || containsNode(this.dropdown.current, event.relatedTarget)) {
        return;
      }
    } else if (containsNode(this.anchor.current, event.target)) {
      return;
    }
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
    if (this.isItMounted) {
      this.setState({
        isFocused: false,
        isOpen: false,
        isPseudoFocused: false,
        inputValue: this.props.onBlurResetsInput ? '' : this.state.inputValue,
        openedViaKeyboard: false
      });
    }
  };
  handleClickOutside = (event: MouseEvent) => {
    if (this.justSelected) {
      this.justSelected = false;
      return;
    }
    if (containsNode(this.dropdown.current, event.target)) return;
    const isFocused = this.state.isFocused || this.state.isPseudoFocused;
    if (isFocused && !containsNode(this.anchor.current, event.target)) {
      this.handleBlur(event);
    }
  };
  handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    let newInputValue = event.target.value;
    this.setState({
      inputValue: newInputValue,
      isOpen: true,
      isPseudoFocused: false,
      openedViaKeyboard: false
    });
    if (this.props.onInputChange) {
      this.props.onInputChange(event);
    }
  };
  handleKeyDown = (event: KeyboardEvent) => {
    if (this.props.disabled) return;
    switch (event.keyCode) {
      case 8:
        // backspace
        if (!this.state.inputValue && this.props.clearable && this.props.backspaceRemoves) {
          event.preventDefault();
          this.backspaceValue();
        }
        break;
      case 9:
        // tab
        this.setState(prevState => ({
          isPseudoFocused: false,
          isFocused: false,
          isOpen: false,
          openedViaKeyboard: false,
          inputValue: !this.props.onCloseResetsInput || !this.props.onBlurResetsInput ? prevState.inputValue : ''
        }));
        break;
      case 27:
        // escape
        if (!this.state.isOpen && this.props.clearable && this.props.escapeClearsValue) {
          this.clearValue(event);
          this.setState({
            isFocused: false,
            isPseudoFocused: false
          });
        }
        break;
      case 32:
        // space
        if (this.props.searchable) {
          break;
        }
        event.preventDefault();
        if (!this.state.isOpen) {
          this.setState({
            isOpen: true,
            openedViaKeyboard: true
          });
        }
        break;
      case 38:
        // up
        event.preventDefault();
        if (!this.state.isOpen) {
          this.setState({
            isOpen: true,
            openedViaKeyboard: true
          });
        }
        break;
      case 40:
        // down
        event.preventDefault();
        if (!this.state.isOpen) {
          this.setState({
            isOpen: true,
            openedViaKeyboard: true
          });
        }
        break;
      case 33:
        // page up
        event.preventDefault();
        if (!this.state.isOpen) {
          this.setState({
            isOpen: true,
            openedViaKeyboard: true
          });
        }
        break;
      case 34:
        // page down
        event.preventDefault();
        if (!this.state.isOpen) {
          this.setState({
            isOpen: true,
            openedViaKeyboard: true
          });
        }
        break;
      case 35:
        // end key
        if (event.shiftKey) {
          break;
        }
        event.preventDefault();
        if (!this.state.isOpen) {
          this.setState({
            isOpen: true,
            openedViaKeyboard: true
          });
        }
        break;
      case 36:
        // home key
        if (event.shiftKey) {
          break;
        }
        event.preventDefault();
        if (!this.state.isOpen) {
          this.setState({
            isOpen: true,
            openedViaKeyboard: true
          });
        }
        break;
      case 46:
        // delete
        if (!this.state.inputValue && this.props.deleteRemoves) {
          event.preventDefault();
          this.popValue();
        }
        break;
    }
  };
  getOptionLabel = (locale: Locale, {
    option
  }: {
    option: Option;
    optionState: {
      $selected: boolean;
      $disabled: boolean;
      $isHighlighted: boolean;
    };
  }): React.ReactNode => option.isCreatable ?
  // @ts-ignore
  `${locale.select.create} “${option[this.props.labelKey]}”` :
  // @ts-ignore
  option[this.props.labelKey];
  getValueLabel = ({
    option
  }: {
    option: Option;
    index?: number;
  }): React.ReactNode => {
    // @ts-ignore
    return option[this.props.labelKey];
  };

  /**
   * Extends the value into an array from the given options
   */
  getValueArray(value: Value | undefined): Array<Option> {
    if (!Array.isArray(value)) {
      if (value === null || value === undefined) return [];
      value = [value];
    }
    return value.map(value => expandValue(value, this.props));
  }
  setValue(value: Value, option: Option | undefined | null, type: ChangeAction) {
    if (this.props.onChange) {
      this.props.onChange({
        value,
        option,
        type
      });
    }
  }
  handleActiveDescendantChange = (id?: string) => {
    if (id) {
      this.setState({
        activeDescendant: id
      });
    } else {
      this.setState({
        activeDescendant: null
      });
    }
  };
  handleInputRef = (input: HTMLInputElement) => {
    this.input = input;
    if (typeof this.props.inputRef === 'function') {
      this.props.inputRef(input);
    } else if (this.props.inputRef) {
      // @ts-expect-error todo(flow->ts) MutableRefObject
      this.props.inputRef.current = input;
    }
    if (this.props.controlRef && typeof this.props.controlRef === 'function') {
      // @ts-expect-error todo(flow->ts) according to types this code is not reachable
      this.props.controlRef(input);
    }
  };
  selectValue = ({
    item
  }: {
    item: Option;
  }) => {
    if (item.disabled) {
      return;
    }
    this.justSelected = true;
    // NOTE: we add/set the value in a callback to make sure the
    // input value is empty to avoid styling issues in Chrome
    const updatedValue = this.props.onSelectResetsInput ? '' : this.state.inputValue;
    if (this.props.multi) {
      this.setState({
        inputValue: updatedValue,
        isOpen: !this.props.closeOnSelect
      }, () => {
        const valueArray = this.props.value;
        // @ts-ignore
        if (valueArray.some(i => i[this.props.valueKey] === item[this.props.valueKey])) {
          this.removeValue(item);
        } else {
          this.addValue(item);
        }
      });
    } else {
      this.focus();
      this.setState({
        inputValue: updatedValue,
        isOpen: !this.props.closeOnSelect,
        isFocused: true,
        isPseudoFocused: false
      }, () => {
        this.setValue([item], item, STATE_CHANGE_TYPE.select);
      });
    }
  };
  addValue = (item: Option) => {
    const valueArray = [...this.props.value];
    this.setValue(valueArray.concat(item), item, STATE_CHANGE_TYPE.select);
  };
  backspaceValue = () => {
    const item = this.popValue();
    if (!item) {
      return;
    }
    // @ts-ignore
    const valueLength = this.props.value.length;
    const renderLabel = this.props.getValueLabel || this.getValueLabel;
    const labelForInput = renderLabel({
      option: item,
      index: valueLength - 1
    });
    // label might not be a string, it might be a Node of another kind.
    if (!this.props.backspaceClearsInputValue && typeof labelForInput === 'string') {
      const remainingInput = labelForInput.slice(0, -1);
      this.setState({
        inputValue: remainingInput,
        isOpen: true
      });
    }
  };
  popValue = () => {
    // @ts-ignore
    const valueArray = [...this.props.value];
    const valueLength = valueArray.length;
    if (!valueLength) return;
    if (valueArray[valueLength - 1].clearableValue === false) return;
    const item = valueArray.pop();
    this.setValue(valueArray, item, STATE_CHANGE_TYPE.remove);
    return item;
  };
  removeValue = (item: Option) => {
    const valueArray = [...this.props.value];
    const nextValue = valueArray.filter(
    // @ts-ignore
    i => i[this.props.valueKey] !== item[this.props.valueKey]);
    this.setValue(nextValue, item, STATE_CHANGE_TYPE.remove);
    // @ts-ignore
    const removedLabel = item[this.props.labelKey];
    this.announceValueChange('removed', typeof removedLabel === 'string' ? removedLabel : '', nextValue.length);
    this.focusAffordanceOrInput();
  };
  clearValue = (event: KeyboardEvent | MouseEvent | TouchEvent) => {
    if (isClick(event) && !isLeftClick(event)) return;
    if (this.props.value) {
      const resetValue = this.props.value.filter(item => item.clearableValue === false);
      this.setValue(resetValue, null, STATE_CHANGE_TYPE.clear);
    }
    this.setState({
      inputValue: '',
      isOpen: false,
      openedViaKeyboard: false
    });
    this.announceValueChange('cleared', '', 0);
    this.focusAffordanceOrInput();
    this.focusAfterClear = true;
  };

  // Caret affordance: the clear button is tabbable (tabIndex 0), so keyboard
  // users land on it and must be able to fire it with Enter/Space. VoiceOver /
  // TalkBack synthesize a click on role="button" and are handled by onClick;
  // a real keyboard press on an <svg> does not, so we bridge it here.
  handleClearKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.clearValue(event.nativeEvent);
  };
  shouldShowPlaceholder = () => {
    return !(this.state.inputValue || this.props.value && this.props.value.length);
  };
  shouldShowValue = () => {
    return !this.state.inputValue;
  };
  renderLoading() {
    if (!this.props.isLoading) return;
    const {
      overrides = {}
    } = this.props;
    const [LoadingIndicator, loadingIndicatorProps] = getOverrides(overrides.LoadingIndicator, StyledLoadingIndicator);
    return <LoadingIndicator role="status" {...loadingIndicatorProps}>
        {/* Offscreen content could be defined as styled-component and
          overridable, but I can't think of a good reason for doing so.
          LoadingIndicator children can be overriden if required. */}
        <span style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap',
        border: 0
      }}>
          Loading
        </span>
      </LoadingIndicator>;
  }
  renderValue(valueArray: Value): React.ReactNode | undefined | null | Array<React.ReactNode | undefined | null> {
    const {
      overrides = {}
    } = this.props;
    const sharedProps = this.getSharedProps();
    const renderLabel = this.props.getValueLabel || this.getValueLabel;
    const Value = this.props.valueComponent || Noop;
    if (!valueArray.length) {
      return null;
    }
    if (this.props.multi) {
      // TODO(a11y/i18n): the per-tag remove button's accessible name ("Remove
      // <value>") is hardcoded in the shared Tag primitive (tag/tag.tsx) and is
      // NOT localized — e.g. under es_AR it still reads "Remove Inglés" instead
      // of "Quitar Inglés". Unlike the clear-button label and the removal/cleared
      // aria-live announcements (localized via locale.select.*), this string lives
      // outside Select and cannot be localized without making Tag locale-aware
      // (add a locale.tag key + LocaleContext consumer in Tag). That touches every
      // Tag consumer, so it's deferred; do it there rather than injecting an
      // aria-label override from here so all Tag users benefit.
      return valueArray.map((value, i) => {
        const disabled = sharedProps.$disabled || value.clearableValue === false;
        return <Value value={value}
        // @ts-ignore
        key={`value-${i}-${value[this.props.valueKey]}`} removeValue={() => this.removeValue(value)} disabled={disabled} overrides={{
          Tag: overrides.Tag,
          MultiValue: overrides.MultiValue
        }} {...sharedProps} $disabled={disabled}>
            {renderLabel({
            option: value,
            index: i
          })}
          </Value>;
      });
    } else if (this.shouldShowValue()) {
      return <Value
      // In caretAffordance mode the caret button's accessible name already carries
      // the selected value, so the visible single value must not be a redundant
      // AT stop. (Multi-select tags above stay reachable for their Remove buttons.)
      aria-hidden={this.props.caretAffordance ? true : undefined}
      // @ts-ignore
      value={valueArray[0][this.props.valueKey]} disabled={this.props.disabled} overrides={{
        SingleValue: overrides.SingleValue
      }} {...sharedProps}>
          {renderLabel({
          option: valueArray[0]
        })}
        </Value>;
    }
  }
  renderInput(listboxId: string, valueDisplayId: string, placeholderFallback: string) {
    const {
      overrides = {}
    } = this.props;
    const [InputContainer, inputContainerProps] = getOverrides(overrides.InputContainer, StyledInputContainer);
    const sharedProps = this.getSharedProps();
    const isOpen = this.state.isOpen;
    // @ts-ignore
    const valueArrayForLabel = this.getValueArray(this.props.value);
    const selected = valueArrayForLabel
    // @ts-ignore
    .map(v => v[this.props.labelKey]).join(', ');
    const selectedLabel = selected.length ? `Selected ${selected}. ` : '';
    const label = `${selectedLabel}${this.props['aria-label'] || ''}`;

    // When receivesExternalLabel is true (opt-in), compose aria-labelledby to include both
    // the external label ID and a hidden value span so the screen reader announces
    // "Language preference, English" — matching native <select> behaviour (WCAG 2.5.3).
    // Defaults to false so existing consumers are not affected.
    const externalLabelledBy = this.props.receivesExternalLabel ? this.props['aria-labelledby'] : undefined;
    const composedLabelledBy = externalLabelledBy ? valueArrayForLabel?.length ? `${externalLabelledBy} ${valueDisplayId}` : externalLabelledBy : undefined;

    // When receivesExternalLabel is true, suppress the "Selected X." aria-label prefix
    // (the external label association takes over, and the visually-hidden span carries the
    // value). When false, use the original fallback behaviour.
    const hasExternalLabel = this.props.receivesExternalLabel && Boolean(externalLabelledBy || this.props.id);
    const ariaLabel = hasExternalLabel ? this.props['aria-label'] || '' : label;

    // Visually hidden span trick to get the value read after the label is read.
    const valueDisplaySpan = externalLabelledBy && valueArrayForLabel?.length ? <span id={valueDisplayId} aria-hidden="true" style={{
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0,0,0,0)',
      whiteSpace: 'nowrap',
      border: 0
    }}>
          {/*
            Render the option label nodes directly (not a `.join`-ed string) so labels
            that are React elements (e.g. <Tag>...</Tag>) contribute their real text
            content to the accessible name rather than coercing to "[object Object]".
            For multi-select, all selected values are rendered with ", " separators.
           */}
          {valueArrayForLabel?.map((option, index) => <React.Fragment
      // @ts-ignore
      key={option[this.props.valueKey] ?? index}>
              {/**
               * JSX based-join needed , as the label
               * rendered is sometimes not a string, but a JSX element
               **/}
              {index > 0 && ', '}
              {/* @ts-ignore */}
              {option[this.props.labelKey]}
            </React.Fragment>)}
        </span> : null;

    // In caretAffordance mode the searchable input stays aria-hidden (so the
    // VoiceOver cursor skips it and the caret button remains the primary control),
    // but sighted keyboard users still Tab onto it. Give it a real accessible name
    // + a native placeholder so that the forced focus announcement identifies the
    // field clearly (e.g. "Select time zone") instead of a bare, contextless
    // "combobox". Mirrors the caret button's naming (aria-labelledby in a
    // FormControl / receivesExternalLabel world, else aria-label) minus the caret's
    // "open" action word, which describes the button's activation, not a text field.
    const caretInputLabelledBy = externalLabelledBy ? composedLabelledBy : undefined;
    const caretInputLabel = externalLabelledBy ? undefined : [this.props['aria-label'], selected].filter(Boolean).join(', ') || undefined;
    if (!this.props.searchable) {
      // When caretAffordance is active the caret button is the AT-visible trigger;
      // hide the combobox input entirely from assistive technology so screen
      // readers encounter only the button and do not double-announce.
      const caretAffordance = this.props.caretAffordance;
      return <InputContainer onFocus={this.handleInputFocus}
      // In caretAffordance mode the caret button is the sole focus stop, so the
      // input wrapper must not be a tab stop (it is also aria-hidden below).
      tabIndex={caretAffordance ? -1 : 0} aria-hidden={caretAffordance ? true : undefined} {...sharedProps} {...inputContainerProps}>
          {valueDisplaySpan}
          {/* $FlowExpectedError[cannot-spread-inexact] */}
          <input aria-activedescendant={caretAffordance ? undefined : this.state.activeDescendant} aria-controls={caretAffordance ? undefined : this.state.isOpen ? listboxId : null} aria-describedby={caretAffordance ? undefined : this.props['aria-describedby']} aria-errormessage={caretAffordance ? undefined : this.props['aria-errormessage']} aria-disabled={caretAffordance ? undefined : this.props.disabled} aria-expanded={caretAffordance ? undefined : isOpen} aria-haspopup={caretAffordance ? undefined : 'listbox'} aria-label={caretAffordance ? undefined : ariaLabel} aria-labelledby={caretAffordance ? undefined : composedLabelledBy ?? this.props['aria-labelledby']} aria-readonly={caretAffordance ? undefined : true} aria-required={caretAffordance ? undefined : this.props.required || null} role={caretAffordance ? undefined : 'combobox'}
        // @ts-ignore
        id={this.props.id || null} ref={this.handleInputRef} style={{
          opacity: 0,
          width: 0,
          overflow: 'hidden',
          border: 'none',
          padding: 0
        }} tabIndex={-1} {...overrides.Input ? overrides.Input.props ?
        // $FlowExpectedError[not-an-object]
        overrides.Input.props : {} : {}} />
        </InputContainer>;
    }

    // When caretAffordance is active on a searchable Select, hide the combobox
    // from AT so screen readers only interact with the caret button.
    const caretAffordance = this.props.caretAffordance;
    return <InputContainer aria-hidden={caretAffordance ? true : undefined} {...sharedProps} {...inputContainerProps}>
        {valueDisplaySpan}
        {/* @ts-ignore */}
        <AutosizeInput
      // Caret affordance: keep the search input tabbable (tabIndex={0}) for
      // sighted keyboard users, but mark it aria-hidden DIRECTLY on the element
      // so the VoiceOver cursor does not land on / announce it during swipe
      // navigation. An aria-hidden ancestor is not enough — VoiceOver follows
      // DOM focus on Tab and will announce a focusable descendant unless the
      // focusable element itself is aria-hidden.
      //
      // We DO give it a real accessible name (aria-label / aria-labelledby,
      // matching the caret button minus its "open" action word) plus a native
      // placeholder, so that when a sighted keyboard user Tabs onto it the
      // forced focus announcement identifies the field ("Select time zone")
      // rather than a bare, contextless "combobox". role="combobox" is kept
      // (matching the phone-input country picker); aria-autocomplete/-expanded
      // stay cleared so the aria-hidden combobox is not re-completed into the
      // verbose "pop-up menu, list box" announcement. The caret button remains
      // the primary AT trigger.
      aria-hidden={caretAffordance ? true : undefined} aria-activedescendant={caretAffordance ? undefined : this.state.activeDescendant} aria-autocomplete={caretAffordance ? undefined : 'list'} aria-controls={caretAffordance ? undefined : this.state.isOpen ? listboxId : null} aria-describedby={caretAffordance ? undefined : this.props['aria-describedby']} aria-errormessage={caretAffordance ? undefined : this.props['aria-errormessage']} aria-expanded={caretAffordance ? undefined : isOpen} aria-haspopup={caretAffordance ? undefined : 'listbox'} aria-label={caretAffordance ? caretInputLabel : ariaLabel} aria-labelledby={caretAffordance ? caretInputLabelledBy : composedLabelledBy ?? this.props['aria-labelledby']}
      // Keep the native placeholder as the sighted-keyboard Tab stop's hint,
      // but only while the field is genuinely empty. The visible placeholder
      // is actually painted by the StyledPlaceholder overlay below; the
      // native one is hidden (transparent) via the Input override to avoid
      // double placeholders — see that comment. shouldShowPlaceholder() gates
      // on both a selected value and in-progress input text; AT naming comes
      // from aria-label/aria-labelledby, so this is purely visual.
      placeholder={caretAffordance && this.shouldShowPlaceholder() ? placeholderFallback : undefined} aria-required={caretAffordance ? undefined : this.props.required || null} disabled={this.props.disabled || null} id={this.props.id || null} inputRef={this.handleInputRef} onChange={this.handleInputChange} onFocus={this.handleInputFocus} onKeyDown={caretAffordance ? this.handleSearchInputKeyDown : undefined}
      // AutosizeInput sizes the field to its VALUE only (the width sizer never
      // includes the placeholder), so on an empty caret-mode input the native
      // placeholder text overflows the ~2px-wide field and visually spills
      // past it — doubling the StyledPlaceholder overlay (e.g. TimePicker's
      // "HH:mm") or peeking out past multi-select tags. Hide the native
      // placeholder text (transparent) so the overlay is the single visible
      // placeholder. Scoped to caretAffordance; consumer Input overrides still
      // win via mergeOverrides.
      overrides={caretAffordance ? mergeOverrides({
        Input: {
          style: {
            '::placeholder': {
              color: 'transparent'
            }
          }
        }
      }, overrides.Input ? {
        Input: overrides.Input
      } : {}) : overrides.Input ? {
        Input: overrides.Input
      } : {}}
      // @ts-ignore
      required={this.props.required && !this.props.value.length || null}
      // role="combobox" is kept even in caret mode (see the aria-hidden note
      // above): an aria-hidden combobox degrades to a generic "group" on Tab in
      // VoiceOver rather than announcing as an "edit text" field.
      role={'combobox'} value={this.state.inputValue} tabIndex={0} {...sharedProps} />
      </InputContainer>;
  }
  renderClear(locale: Locale) {
    const isValueEntered = Boolean(this.props.value && this.props.value.length || this.state.inputValue);
    if (!this.props.clearable || this.props.disabled || this.props.isLoading || !isValueEntered) {
      return;
    }
    const {
      $size,
      ...sharedProps
    } = this.getSharedProps();
    const {
      overrides = {}
    } = this.props;
    const [ClearIcon, clearIconProps] = getOverrides(overrides.ClearIcon, DeleteAlt);
    const ariaLabel = this.props.multi ? locale.select.clearAll ?? 'Clear all' : locale.select.clearValue ?? 'Clear value';
    const sizes = {
      [SIZE.mini]: 15,
      [SIZE.compact]: 15,
      [SIZE.default]: 18,
      [SIZE.large]: 22
    };

    // Caret affordance keeps the combobox input hidden from AT, so the clear
    // button must sit in the tab order to stay keyboard/screen-reader reachable.
    // Scoped to caretAffordance so the default Select (thousands of consumers)
    // keeps its existing tab order. onKeyDown bridges real keyboard Enter/Space.
    const clearButtonTabIndex = this.props.caretAffordance ? 0 : undefined;
    return <ClearIcon title={ariaLabel} aria-label={ariaLabel} tabIndex={clearButtonTabIndex} onClick={this.clearValue} onKeyDown={this.props.caretAffordance ? this.handleClearKeyDown : undefined} role="button"
    // @ts-ignore
    size={sizes[this.props.size] || sizes[SIZE.default]} {...sharedProps} {...clearIconProps} />;
  }
  handleCaretClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (this.props.disabled) return;
    // Only mark as affordance-open when the click will open the dropdown.
    // If it will close (toggle from open), leave the flags false.
    if (!this.state.isOpen) {
      this._openedViaAffordance = true;
      // Set BEFORE setState so the menu captures keyboardControlNode=null at
      // construction and the listbox owns its keydowns from the first frame.
      this._keyboardFocusInListbox = true;
    }
    this.setState(s => ({
      isOpen: !s.isOpen
    }));
  };

  // Caret affordance only: hand the type-to-search input off to the listbox on
  // the first Arrow key. While focus sits on the (aria-hidden) search input the
  // menu tracks its highlight only virtually — aria-activedescendant on the
  // input — which never reaches assistive tech and never moves real DOM focus
  // into the list. So on ArrowUp/ArrowDown we mirror a caret open: flip
  // _keyboardFocusInListbox (re-pointing the menu's keyboardControlNode at the
  // listbox so the <ul> owns its own keydowns) and move real focus onto the
  // active option so VoiceOver/TalkBack follow arrow navigation from there.
  handleSearchInputKeyDown = (event: React.KeyboardEvent) => {
    if (this.props.disabled || !this.props.caretAffordance) return;
    // Already handed off — the listbox owns the keys now.
    if (this._keyboardFocusInListbox) return;
    const {
      key
    } = event;
    if (key !== 'ArrowDown' && key !== 'ArrowUp') return;
    event.preventDefault();
    const ownerDoc = this._caretRef.current?.ownerDocument ?? document;
    if (!this.state.isOpen) {
      // Closed (e.g. Tab'd onto the input, no query yet): open like the caret so
      // componentDidUpdate → focusSelectedOptionAfterAffordanceOpen lands focus on
      // the selected option.
      this._openedViaAffordance = true;
      this._keyboardFocusInListbox = true;
      this.setState({
        isOpen: true
      });
      return;
    }
    // Open via typing: the menu is already mounted with keyboardControlNode=anchor.
    // Flip the flag and re-render so keyboardControlNode becomes {current:null}
    // (the <ul> starts handling its own keydowns), then pull real focus onto the
    // currently highlighted option.
    this._keyboardFocusInListbox = true;
    this._sessionHighlightMovedRef.current = false;
    this.forceUpdate(() => {
      focusActiveListboxOption(ownerDoc, this._listboxId, this.state.activeDescendant);
    });
  };
  handleCaretKeyDown = (event: React.KeyboardEvent) => {
    if (this.props.disabled) return;
    const {
      key
    } = event;
    if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      if (!this.state.isOpen) {
        // Dropdown is closed: open it and arrange for focus to move onto the
        // selected option via componentDidUpdate → focusSelectedOptionAfterAffordanceOpen.
        this._openedViaAffordance = true;
        // Set BEFORE setState so the menu captures keyboardControlNode=null at
        // construction and the listbox owns its keydowns from the first frame.
        this._keyboardFocusInListbox = true;
        this.setState({
          isOpen: true
        });
      }
      // If already open, do NOT call setState again — let the event bubble up
      // through the ControlContainer so the StatefulMenu's onKeyDown listener
      // (attached to the <ul> when keyboardControlNode is null) handles
      // navigation / selection naturally.
    } else if (key === 'Escape') {
      this.setState({
        isOpen: false
      });
    }
  };
  renderArrow(listboxId: string, isOpen: boolean | undefined, caretValueId: string, caretActionId: string, valueArray: Value) {
    if (this.props.type !== TYPE.select) {
      return null;
    }
    const {
      $size,
      ...sharedProps
    } = this.getSharedProps();
    const {
      overrides = {}
    } = this.props;
    const [SelectArrow, selectArrowProps] = getOverrides(overrides.SelectArrow, TriangleDownIcon);
    selectArrowProps.overrides = mergeOverrides({
      Svg: {
        style: ({
          $theme,
          $disabled
        }) => {
          return {
            color: $disabled ? $theme.colors.inputTextDisabled : $theme.colors.contentPrimary
          };
        }
      }
    }, selectArrowProps.overrides);
    const sizes = {
      [SIZE.mini]: 16,
      [SIZE.compact]: 16,
      [SIZE.default]: 20,
      [SIZE.large]: 24
    };
    if (this.props.caretAffordance) {
      // Render the chevron as an explicit interactive button so Android TalkBack
      // and iOS VoiceOver have a reliable double-tap target. The combobox input
      // and value container are hidden from AT when this mode is active.
      //
      // Accessible name composition:
      //   - With FormControl (receivesExternalLabel + aria-labelledby):
      //       aria-labelledby = "[field-label-id] [value-span-id?] [action-span-id]"
      //       → "Language preference, English, open"
      //   - Standalone (aria-label prop only):
      //       aria-label = "[field-label], [selected value], open"
      //       → "Select a time, 12-hour format., 2:30 PM, open"
      const externalLabelledBy = this.props.receivesExternalLabel ? this.props['aria-labelledby'] : undefined;
      let caretAriaLabelledBy: string | undefined;
      let caretAriaLabel: string | undefined;
      if (externalLabelledBy) {
        caretAriaLabelledBy = [externalLabelledBy, valueArray.length > 0 ? caretValueId : null, caretActionId].filter(Boolean).join(' ');
      } else {
        // For React-element labels, we can only extract string content here;
        // fall back to an empty string for non-string labels.
        const selectedText = valueArray
        // @ts-ignore
        .map(v => {
          const lbl = v[this.props.labelKey ?? 'label'];
          return typeof lbl === 'string' ? lbl : '';
        }).filter(Boolean).join(', ');
        caretAriaLabel = [this.props['aria-label'], selectedText, 'open'].filter(Boolean).join(', ');
      }

      // The interactive trigger is the DIV wrapping the chevron (not the <svg>
      // itself), mirroring the phone-input country picker. This lets us grow the
      // pointer/focus hit target to a WCAG 2.5.8 (Target Size, Minimum) compliant
      // ≥24×24 CSS px without resizing the visible chevron. The chevron sizes are
      // 16px (mini/compact), 20px (default) or 24px (large) — the first three are
      // below 24px, so we pad the wrapper out to 24px wide with paddingInline and
      // cancel that padding's layout contribution with a matching negative
      // marginInline, leaving the control's overall width visually unchanged. The
      // wrapper stretches to the full control height (alignSelf: stretch), so the
      // vertical target already meets 24px in every size. The <svg> becomes
      // decorative (aria-hidden); the button semantics/name live on the wrapper.
      const arrowIconPx = sizes[this.props.size ?? SIZE.default] || sizes[SIZE.default];
      const caretInlinePad = Math.max(0, Math.ceil((24 - arrowIconPx) / 2));
      return <div role="button" tabIndex={this.props.disabled ? -1 : 0} aria-haspopup="listbox" aria-expanded={isOpen} aria-controls={isOpen ? listboxId : undefined} aria-labelledby={caretAriaLabelledBy} aria-label={caretAriaLabel} aria-disabled={this.props.disabled || undefined} ref={(this._caretRef as React.RefObject<HTMLDivElement>)} onClick={this.handleCaretClick} onKeyDown={this.handleCaretKeyDown} style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'stretch',
        cursor: this.props.disabled ? 'not-allowed' : 'pointer',
        paddingInlineStart: `${caretInlinePad}px`,
        paddingInlineEnd: `${caretInlinePad}px`,
        marginInlineStart: `-${caretInlinePad}px`,
        marginInlineEnd: `-${caretInlinePad}px`
      }}>
          <SelectArrow
        // @ts-ignore
        size={arrowIconPx} aria-hidden={true} {...sharedProps} {...selectArrowProps} />
        </div>;
    }
    return <SelectArrow
    // @ts-ignore
    size={sizes[this.props.size] || sizes[SIZE.default]} title={'open'}
    // This non-interactive arrow down icon should be invisible to screen readers
    aria-hidden={true} {...sharedProps} {...selectArrowProps} />;
  }
  renderSearch() {
    if (this.props.type !== TYPE.search) {
      return null;
    }
    const {
      overrides = {}
    } = this.props;
    const [SearchIconContainer, searchIconContainerProps] = getOverrides(overrides.SearchIconContainer, StyledSearchIconContainer);
    const [SearchIcon, searchIconProps] = getOverrides(overrides.SearchIcon, SearchIconComponent);
    const sharedProps = this.getSharedProps();
    return <SearchIconContainer {...sharedProps} {...searchIconContainerProps}>
        <SearchIcon size={16} title={'search'} {...sharedProps} {...searchIconProps} />
      </SearchIconContainer>;
  }
  filterOptions(excludeOptions?: Value | null) {
    const filterValue = this.state.inputValue.trim();
    // apply filter function
    if (this.props.filterOptions) {
      this.options = this.props.filterOptions(this.options, filterValue, excludeOptions, {
        // @ts-ignore
        valueKey: this.props.valueKey,
        // @ts-ignore
        labelKey: this.props.labelKey
      });
    }
    // can user create a new option + there's no exact match already
    const filterDoesNotMatchOption = this.props.ignoreCase ?
    // @ts-ignore
    opt => opt[this.props.labelKey].toLowerCase() !== filterValue.toLowerCase().trim() :
    // @ts-ignore
    opt => opt[this.props.labelKey] !== filterValue.trim();
    if (filterValue && this.props.creatable &&
    // @ts-ignore
    this.options.concat(this.props.value).every(filterDoesNotMatchOption)) {
      // @ts-expect-error todo(flow->ts) this.options is typed as a read-only array
      this.options.push({
        id: filterValue,
        // @ts-ignore
        [this.props.labelKey]: filterValue,
        // @ts-ignore
        [this.props.valueKey]: filterValue,
        isCreatable: true
      });
    }
    return this.options;
  }
  getSharedProps() {
    const {
      clearable,
      creatable,
      disabled,
      error,
      positive,
      isLoading,
      multi,
      required,
      size,
      searchable,
      type,
      value
    } = this.props;
    const {
      isOpen,
      isFocused,
      isPseudoFocused
    } = this.state;
    return {
      $clearable: clearable,
      $creatable: creatable,
      $disabled: disabled,
      $error: error,
      $positive: positive,
      $isFocused: isFocused,
      $isLoading: isLoading,
      $isOpen: isOpen,
      $isPseudoFocused: isPseudoFocused,
      $multi: multi,
      $required: required,
      $searchable: searchable,
      $size: size,
      $type: type,
      // @ts-ignore
      $isEmpty: !this.getValueArray(value).length
    };
  }
  render() {
    this.options = normalizeOptions(this.props.options);
    const {
      overrides = {},
      type,
      multi,
      noResultsMsg,
      value,
      filterOutSelected
    } = this.props;
    if (__DEV__) {
      // value may be nullish, only warn if value is defined
      if (value && !Array.isArray(value)) {
        console.warn('The Select component expects an array as the value prop. For more information, please visit the docs at https://baseweb.design/components/select/');
      }
    }
    const [Root, rootProps] = getOverrides(overrides.Root, StyledRoot);
    const [ControlContainer, controlContainerProps] = getOverrides(overrides.ControlContainer, StyledControlContainer);
    const [ValueContainer, valueContainerProps] = getOverrides(overrides.ValueContainer, StyledValueContainer);
    const [IconsContainer, iconsContainerProps] = getOverrides(overrides.IconsContainer, StyledIconsContainer);
    const [PopoverOverride, popoverProps] = getOverrides(overrides.Popover, Popover);
    const [Placeholder, placeholderProps] = getOverrides(overrides.Placeholder, StyledPlaceholder);
    const sharedProps = this.getSharedProps();

    // @ts-ignore
    const valueArray = this.getValueArray(value);
    const options = this.filterOptions(multi && filterOutSelected ? valueArray : null);
    const isOpen = this.state.isOpen;
    sharedProps.$isOpen = isOpen;
    if (__DEV__) {
      if (this.props.error && this.props.positive) {
        console.warn(`[Select] \`error\` and \`positive\` are both set to \`true\`. \`error\` will take precedence but this may not be what you want.`);
      }
    }
    return <UIDConsumer>
        {(listboxId: string) => {
        // Derive a stable ID for the hidden value-display span. Used by renderInput to compose
        // aria-labelledby as "{external-label-id} {valueDisplayId}" so the screen reader
        // announces both the field label and the current value (e.g. "Language preference, English").
        const valueDisplayId = `${listboxId}-value`;
        // Store for componentDidUpdate so it can find the listbox after open.
        this._listboxId = listboxId;
        // IDs for the caret affordance accessible name spans (field value + "open" action).
        const caretValueId = `${listboxId}-caret-value`;
        const caretActionId = `${listboxId}-caret-action`;
        return <LocaleContext.Consumer>
              {locale => <PopoverOverride
          // Popover does not provide ability to forward refs through, and if we were to simply
          // apply the ref to the Root component below it would be overwritten before the popover
          // renders it. Using this strategy, we will get a ref to the popover, then reuse its
          // anchorRef so we can check if clicks are on the select component or not.
          innerRef={(ref: any) => {
            if (!ref) return;
            this.anchor = ref.anchorRef;
          }} accessibilityType={ACCESSIBILITY_TYPE.none} autoFocus={false} focusLock={false} mountNode={this.props.mountNode} onEsc={() => this.closeMenu()} isOpen={isOpen} popoverMargin={0} content={() => {
            // In caret-affordance mode, layer on the accessibility
            // overrides that make the dropdown navigable by iOS
            // VoiceOver / TalkBack (scoped here so the default Select is
            // untouched):
            //   - DropdownListItem: roving-tabindex item that moves real
            //     DOM focus to the highlighted option on keyboard nav.
            //   - Dropdown (List root): neutralize the menu's
            //     focusMenu/unfocusMenu (onFocus/onBlur no-ops) so a
            //     swipe doesn't yank focus back to the <ul> and re-center
            //     (the scroll-lock-to-top fix), and take the <ul> itself
            //     out of the tab order.
            //   - StatefulMenu: a stateReducer that flips the session
            //     "highlight moved" ref so the item centers on the
            //     initial open only.
            const dropdownOverrides = this.props.caretAffordance ? mergeOverrides({
              // @ts-ignore
              DropdownListItem: {
                component: CaretAffordanceDropdownListItem
              },
              Dropdown: {
                props: {
                  tabIndex: -1,
                  onFocus: () => {},
                  onBlur: () => {}
                }
              },
              StatefulMenu: {
                props: {
                  // @ts-ignore
                  stateReducer: (changeType, changes) => {
                    if (changeType === MENU_STATE_CHANGE_TYPES.moveUp || changeType === MENU_STATE_CHANGE_TYPES.moveDown || changeType === MENU_STATE_CHANGE_TYPES.mouseEnter || changeType === MENU_STATE_CHANGE_TYPES.character || changeType === MENU_STATE_CHANGE_TYPES.click) {
                      this._markSessionHighlightMoved();
                    }
                    return changes;
                  }
                }
              }
            },
            // @ts-ignore
            overrides || {}) : overrides;
            const dropdownProps = {
              error: this.props.error,
              positive: this.props.positive,
              getOptionLabel: this.props.getOptionLabel || this.getOptionLabel.bind(this, locale),
              id: listboxId,
              isLoading: this.props.isLoading,
              labelKey: this.props.labelKey,
              maxDropdownHeight: this.props.maxDropdownHeight,
              multi,
              noResultsMsg,
              onActiveDescendantChange: this.handleActiveDescendantChange,
              onItemSelect: this.selectValue,
              options,
              overrides: dropdownOverrides,
              required: this.props.required,
              searchable: this.props.searchable,
              size: this.props.size,
              type,
              value: valueArray,
              valueKey: this.props.valueKey,
              width: this.anchor.current ? this.anchor.current.clientWidth : null,
              // While a caret affordance open routes focus into the
              // listbox, pass keyboardControlNode=null so the <ul> owns
              // its onKeyDown (focus is on a portalled <li>, not the
              // ControlContainer the default DOM listener binds to).
              keyboardControlNode: this.props.caretAffordance && this._keyboardFocusInListbox ? {
                current: null
              } : this.anchor,
              openedViaKeyboard: this.state.openedViaKeyboard
            };

            // @ts-ignore
            const dropdown = <SelectDropdown innerRef={this.dropdown} {...dropdownProps} />;
            // Provide the per-session highlight-moved ref to every caret
            // list item via context (survives the portal; avoids the
            // deepMerge ref-cloning trap of passing it as an override).
            return this.props.caretAffordance ? <SelectCaretSessionContext.Provider value={this._sessionHighlightMovedRef}>
                        {dropdown}
                      </SelectCaretSessionContext.Provider> : dropdown;
          }} placement={PLACEMENT.bottom} {...popoverProps}>
                  <Root onBlur={this.handleBlur} data-baseweb="select" {...sharedProps} {...rootProps}>
                    <ControlContainer onKeyDown={this.handleKeyDown} onClick={this.handleClick} onTouchEnd={this.handleTouchEnd} onTouchMove={this.handleTouchMove} onTouchStart={this.handleTouchStart} {...sharedProps} {...controlContainerProps}>
                      {type === TYPE.search ? this.renderSearch() : null}

                      {/* When caretAffordance is active on a single-select, hide the
                          value/placeholder area from AT — the caret button's accessible name
                          carries the label + value + action, so it would be a redundant
                          extra traversal stop.
                          For multi-select we must NOT hide the container because the Tag
                          "Remove X" buttons inside need to remain reachable by screen readers. */}
                      <ValueContainer aria-hidden={this.props.caretAffordance && !this.props.multi ? true : undefined} {...sharedProps} {...valueContainerProps}>
                        {this.renderValue(valueArray)}
                        {this.renderInput(listboxId, valueDisplayId, typeof this.props.placeholder !== 'undefined' ? String(this.props.placeholder) : locale.select.placeholder)}
                        {this.shouldShowPlaceholder() ? <Placeholder aria-hidden={this.props.caretAffordance ? true : undefined} {...sharedProps} {...placeholderProps}>
                            {typeof this.props.placeholder !== 'undefined' ? this.props.placeholder : locale.select.placeholder}
                          </Placeholder> : null}
                      </ValueContainer>

                      <IconsContainer {...sharedProps} {...iconsContainerProps}>
                        {this.renderLoading()}
                        {this.renderClear(locale)}
                        {type === TYPE.select ? this.renderArrow(listboxId, isOpen, caretValueId, caretActionId, valueArray) : null}
                      </IconsContainer>
                    </ControlContainer>

                    {/* Visually-hidden spans used by the caret button's aria-labelledby to
                        compose its accessible name ("[field label] [selected value] open").
                        Rendered only in caretAffordance mode so there is no DOM overhead in
                        the default combobox path. */}
                    {this.props.caretAffordance && valueArray.length > 0 && <span id={caretValueId} aria-hidden="true" style={VISUALLY_HIDDEN}>
                        {valueArray.map((option, index) => <React.Fragment
                // @ts-ignore
                key={option[this.props.valueKey] ?? index}>
                            {index > 0 && ', '}
                            {/* @ts-ignore */}
                            {option[this.props.labelKey]}
                          </React.Fragment>)}
                      </span>}
                    {this.props.caretAffordance && <span id={caretActionId} aria-hidden="true" style={VISUALLY_HIDDEN}>
                        open
                      </span>}
                    {/* Polite live region: announces value changes so screen-reader
                        users get spoken confirmation of a removal / clear even when the
                        AT cursor can't be moved onto the caret (Android TalkBack). The
                        message is composed here (LocaleContext in scope) with an English
                        fallback; a trailing zero-width space, toggled by ariaLiveNonce,
                        forces re-announcement of repeated messages without being voiced. */}
                    {this.props.caretAffordance && <span aria-live="polite" aria-atomic="true" role="status" style={VISUALLY_HIDDEN}>
                        {this.composeAriaLiveMessage(locale)}
                      </span>}
                  </Root>
                </PopoverOverride>}
            </LocaleContext.Consumer>;
      }}
      </UIDConsumer>;
  }
}
export default Select;